import { skipToken } from '@reduxjs/toolkit/query';
import useAuth from 'hooks/useAuth';
import { useGetListViewsQuery } from 'store/api/listViews';
import type { FigureRange, WidgetFigure } from 'types/homeWidgetFigures';
import type { SavedViewDefinition } from 'types/homeWidgets';
import type { SimpleCurrencyDto } from 'types/other';
import { secondsToHours } from 'utils/time';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { periodRange, previousPeriodRange } from './periodRange';
import { type ListAnswer, useGetSavedViewAnswerQuery } from './savedViewApi';
import { forViewer, type ListTotal, SAVED_VIEW_LISTS } from './savedViewLists';
import { viewFilter, withDates } from './savedViewQuery';
import type { WidgetFigureState } from './useWidgetFigure';
import type { CountedView } from './widgetLink';

/** One number read off a list's answer. `capped`: the count stopped at the list's cap, and the list holds more. */
type Reading = { value: number; currencyCode?: string; capped?: boolean };

function countOf(answer: ListAnswer): Reading {
    return { value: Number(answer.totalElements ?? 0), capped: answer.totalCountAccurate === false };
}

/**
 * One total of a list's totals answer, as the list's own totals row reads it: seconds as hours rounded the list's
 * way, an amount in the currency the answer names. Nothing when the list holds too many rows to total, or names
 * currencies it could not convert and so left out of the total.
 */
function totalOf(answer: ListAnswer, { key, unit }: ListTotal, hoursScale: 1 | 2): Reading | undefined {
    if (answer.limitExceeded === true) return undefined;
    if (Array.isArray(answer.excludedCurrencies) && answer.excludedCurrencies.length > 0) return undefined;
    const raw = answer[key];
    if (Array.isArray(raw)) {
        // ponytail: a list totalled per currency (bank transfers, never converted) gives its first currency's
        // amount. A view holding several needs a currency in the definition.
        const [first] = raw as SimpleCurrencyDto[];
        return { value: Number(first?.amount ?? 0), currencyCode: first?.currencyTypeCode };
    }
    const value = Number(raw ?? 0);
    if (unit === 'HOURS') return { value: secondsToHours(value, hoursScale) };
    return {
        value,
        currencyCode: typeof answer.baseCurrencyTypeCode === 'string' ? answer.baseCurrencyTypeCode : undefined,
    };
}

function isForbidden(error: unknown): boolean {
    return !!error && typeof error === 'object' && 'status' in error && error.status === 403;
}

/**
 * The figure of a widget built on a list's view: the view's row count, or one of its totals, asked from the list's
 * own endpoints with the filter the list sends for that view. A period replaces the view's own dates and brings the
 * same figure over the period before; without one the view's dates stand and there is nothing to compare with. A
 * view that is gone or was never shared with the viewer, and a list closed to them, read as a figure they cannot
 * see. Without a definition it asks for nothing.
 */
export function useSavedViewFigure(definition: SavedViewDefinition | undefined, asOf: string): WidgetFigureState {
    const viewerId = useAuth().user?.id;
    const { displayDecimalScale } = useTimecardPrecision();
    const list = definition ? SAVED_VIEW_LISTS[definition.listKey] : undefined;
    const savedId = typeof definition?.view === 'number' ? definition.view : undefined;
    const views = useGetListViewsQuery(definition && list && savedId !== undefined ? definition.listKey : skipToken);

    const saved = views.data?.views.find((candidate) => candidate.id === savedId);
    // A saved view is filtered as the built-in view it is based on.
    const builtInKey = saved ? saved.baseViewKey : savedId === undefined ? definition?.view : undefined;
    const builtIn = list?.views.find((candidate) => candidate.id === builtInKey);
    const view: CountedView | undefined = saved
        ? { id: saved.id, baseViewKey: saved.baseViewKey, query: saved.state.query }
        : builtIn && savedId === undefined
          ? { id: builtIn.id, baseViewKey: builtIn.id, query: forViewer(builtIn.searchParams, viewerId) }
          : undefined;

    const dated = definition && definition.period !== 'AS_SAVED' ? definition.period : undefined;
    const period = dated && periodRange(dated, asOf);
    const previousPeriod = dated && previousPeriodRange(dated, asOf);
    const total =
        definition?.show === 'TOTAL'
            ? list?.totals?.fields.find((candidate) => candidate.key === definition.total)
            : undefined;
    // What the registry cannot answer: a list it does not hold, a period on a list without a date field, a total
    // the list does not give.
    const dateField = list?.dateField;
    const unsupported = !!definition && (!list || (!!dated && !dateField) || (definition.show === 'TOTAL' && !total));

    const url = total ? list?.totals?.url : list?.url;
    const countUrl = total ? undefined : list?.countUrl;
    const filterOver = (range: FigureRange | undefined, shown: CountedView) => {
        const query = range && dateField ? withDates(shown.query, dateField, range) : shown.query;
        return viewFilter(query, forViewer(builtIn?.passiveFilter, viewerId), list?.joint);
    };
    const requestOver = (range: FigureRange | undefined) => {
        if (!view || !url || unsupported) return skipToken;
        const filter = filterOver(range, view);
        return total ? { url, filter } : { url, filter, size: 1 };
    };
    // As the list's own page does, the exact count is asked for only once the page says it stopped at the cap.
    const exactOver = (range: FigureRange | undefined, answer: ListAnswer | undefined) =>
        view && countUrl && answer?.totalCountAccurate === false
            ? { url: countUrl, filter: filterOver(range, view) }
            : skipToken;
    const current = useGetSavedViewAnswerQuery(requestOver(period));
    const before = useGetSavedViewAnswerQuery(previousPeriod ? requestOver(previousPeriod) : skipToken);
    const currentExact = useGetSavedViewAnswerQuery(exactOver(period, current.currentData));
    const beforeExact = useGetSavedViewAnswerQuery(exactOver(previousPeriod, before.currentData));

    // An exact count that fails leaves the capped one standing.
    const read = (answer: ListAnswer | undefined, exact: ListAnswer | undefined) =>
        answer &&
        (total
            ? totalOf(answer, total, displayDecimalScale)
            : exact
              ? { value: Number(exact.count ?? 0) }
              : countOf(answer));
    const now = read(current.currentData, currentExact.currentData);
    const then = read(before.currentData, beforeExact.currentData);
    // Not `isLoading`, which is first-load only: a changed period would flash the capped figure. The previous
    // period's comparison arrives late, as its page's does.
    const countPending = currentExact.isFetching && !currentExact.currentData;

    const viewClosed = !!definition && !!list && !view && (savedId === undefined || views.isSuccess);
    const closed = viewClosed || isForbidden(current.error);
    const failed = unsupported || views.isError || current.isError || (!!current.currentData && !now);
    const figure: WidgetFigure | undefined =
        now && !closed && !failed && !countPending
            ? {
                  unit: total?.unit ?? 'COUNT',
                  currencyCode: now.currencyCode,
                  value: now.value,
                  // A count past the cap, or an amount in another currency, is nothing to compare with.
                  previousValue:
                      then && !now.capped && !then.capped && then.currencyCode === now.currencyCode
                          ? then.value
                          : undefined,
                  period,
                  previousPeriod,
              }
            : undefined;
    return {
        figure,
        isLoading: !figure && !closed && !failed,
        isRefreshing: !!figure && (current.isFetching || currentExact.isFetching),
        isForbidden: closed,
        isError: failed,
        isCapped: !!figure && !!now?.capped,
        view: figure ? view : undefined,
        retry: () => {
            if (views.isError) void views.refetch();
            else if (!current.isUninitialized) void current.refetch();
        },
    };
}
